/**
 * _core.js — shared infrastructure for all domain service modules.
 *
 * Contains: config, token management, URL builders, fetch helpers, response parsing,
 * endpoint-unavailability tracking, and shared normalizers.
 *
 * Domain service files (auth.js, dashboard.js, etc.) import helpers from here.
 * The top-level api.js barrel re-exports everything so existing consumer imports
 * (`import { foo } from '../api'`) continue to work unchanged.
 */

import { clearAuthenticatedQueryCache } from '../queryClient';

// =============================================================================
// 1. Configuration
// =============================================================================

function resolveApiBaseUrl() {
  const raw = (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');
  const forceDirect = process.env.REACT_APP_DEV_USE_DIRECT_API === 'true';
  if (!raw || forceDirect) return raw;
  if (process.env.NODE_ENV !== 'development') return raw;
  try {
    const u = new URL(raw);
    const host = u.hostname.toLowerCase();
    const port = u.port || (u.protocol === 'https:' ? '443' : '80');
    const isLoopback = host === '127.0.0.1' || host === 'localhost' || host === '::1';
    const isDjangoDevPort = port === '8000';
    const looksLikeV1Api = /^\/api\/v\d+/i.test(u.pathname || '');
    if (isLoopback && isDjangoDevPort && looksLikeV1Api) return '';
  } catch {
    /* leave raw */
  }
  return raw;
}

export const API_BASE_URL = resolveApiBaseUrl();
export const API_ROOT_URL = API_BASE_URL
  ? API_BASE_URL.replace(/\/api\/v\d+$/i, '').replace(/\/+$/, '')
  : '';
const DEBUG_API_LOGS = process.env.REACT_APP_DEBUG_API_LOGS === 'true';

export function debugApiLog(...args) {
  if (DEBUG_API_LOGS) {
    console.log(...args);
  }
}

export function toSameOriginApiIfProxied(url) {
  if (!url || typeof url !== 'string') return url;
  if (API_BASE_URL) return url;
  if (typeof window === 'undefined') return url;
  if (!/^https?:\/\//i.test(url)) return url;
  try {
    const u = new URL(url);
    const pos = u.pathname.indexOf('/api/');
    if (pos !== -1) {
      return `${u.pathname.slice(pos)}${u.search}`;
    }
  } catch {
    /* ignore */
  }
  return url;
}

export function getEffectiveApiBaseDescription() {
  if (API_BASE_URL) return API_BASE_URL;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api/v1 (proxied to Django)`;
  }
  return 'same-origin /api/v1 (dev proxy; see src/setupProxy.js)';
}

// — Idle / absolute-session timeout constants —
export const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
export const IDLE_WARN_BEFORE_MS = 1 * 60 * 1000;
export const SESSION_MAX_MS = 12 * 60 * 60 * 1000;
export const LOGIN_TIME_KEY = 'xspectre_login_time';
export const LAST_ACTIVITY_KEY = 'xspectre_last_activity';

export function recordLoginTime() {
  try { window.localStorage.setItem(LOGIN_TIME_KEY, String(Date.now())); } catch { /* ignore */ }
  try { window.sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch { /* ignore */ }
}

export function clearLoginTime() {
  try { window.localStorage.removeItem(LOGIN_TIME_KEY); } catch { /* ignore */ }
  try { window.sessionStorage.removeItem(LAST_ACTIVITY_KEY); } catch { /* ignore */ }
}

export function getLoginTime() {
  try {
    const raw = window.localStorage.getItem(LOGIN_TIME_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  } catch { return null; }
}

// =============================================================================
// Storage key constants
// =============================================================================

export const ACCESS_TOKEN_KEY = 'xspectre_access_token';
export const REFRESH_TOKEN_KEY = 'xspectre_refresh_token';
export const FORCE_PASSWORD_CHANGE_KEY = 'xspectre_force_password_change_required';
export const PROFILE_CACHE_KEY = 'xspectre_profile_cache';
export const PROFILE_ENDPOINT_MISSING_KEY = 'xspectre_profile_endpoint_missing';
export const NETWORK_SCHEDULE_ENDPOINT_MISSING_KEY = 'xspectre_network_schedule_endpoint_missing';
export const NETWORK_HISTORY_ENDPOINT_UNAVAILABLE_KEY = 'xspectre_network_history_endpoint_unavailable';
export const SITES_ENDPOINT_UNAVAILABLE_KEY = 'xspectre_sites_endpoint_unavailable';
export const NET_SCANNERS_ENDPOINT_UNAVAILABLE_KEY = 'xspectre_net_scanners_endpoint_unavailable';
export const WEB_SCANNERS_ENDPOINT_UNAVAILABLE_KEY = 'xspectre_web_scanners_endpoint_unavailable';
export const VULNS_ENDPOINT_UNAVAILABLE_KEY = 'xspectre_vulns_endpoint_unavailable';
export const HISTORY_ENDPOINT_UNAVAILABLE_KEY = 'xspectre_history_endpoint_unavailable';
export const INVENTORY_DETAIL_ENDPOINT_UNAVAILABLE_KEY = 'xspectre_inventory_detail_endpoint_unavailable';
export const EARLY_INTEL_ENDPOINT_UNAVAILABLE_KEY = 'xspectre_early_intel_endpoint_unavailable';
export const EARLY_INTEL_EXPOSURES_ENDPOINT_UNAVAILABLE_KEY = 'xspectre_early_intel_exposures_endpoint_unavailable';
export const WEB_SCAN_HISTORY_ENDPOINT_UNAVAILABLE_KEY = 'xspectre_web_scan_history_endpoint_unavailable';
const AUTH_EXPIRED_EVENT = 'xspectre-auth-expired';

// Module-level mutable state — accessed via getters/setters for cross-module use.
let _networkHistoryRequestPromise = null;
let _networkScheduleRequestPromise = null;
let refreshInFlightPromise = null;

export function getNetworkHistoryRequestPromise() { return _networkHistoryRequestPromise; }
export function setNetworkHistoryRequestPromise(p) { _networkHistoryRequestPromise = p; }
export function getNetworkScheduleRequestPromise() { return _networkScheduleRequestPromise; }
export function setNetworkScheduleRequestPromise(p) { _networkScheduleRequestPromise = p; }

// =============================================================================
// 2. Token storage (localStorage)
// =============================================================================

export function getStoredAccessToken() {
  try {
    return window.localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getStoredRefreshToken() {
  try {
    return window.localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function decodeJwtPayload(token) {
  const raw = String(token || '').trim();
  if (!raw) return null;
  const parts = raw.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
}

export function buildProfileFromJwtPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;

  const emailCandidates = [
    payload.email,
    payload.upn,
    payload.preferred_username,
    payload.username,
    payload.user_name,
  ];
  const email = emailCandidates.find((value) => typeof value === 'string' && value.includes('@')) || '';

  const firstName = typeof payload.first_name === 'string' ? payload.first_name.trim() : '';
  const lastName = typeof payload.last_name === 'string' ? payload.last_name.trim() : '';
  const joinedName = [firstName, lastName].filter(Boolean).join(' ');
  const nameCandidates = [
    payload.name,
    payload.display_name,
    payload.full_name,
    joinedName,
    payload.username,
    payload.user_name,
    email ? email.split('@')[0] : '',
  ];
  const name = nameCandidates.find((value) => typeof value === 'string' && value.trim()) || '';

  const profile = {
    name,
    display_name: name,
    email,
    first_name: firstName,
    last_name: lastName,
    role: payload.role ?? payload.user_role ?? payload.user?.role ?? '',
    capabilities: payload.capabilities && typeof payload.capabilities === 'object' ? payload.capabilities : null,
    is_staff: payload.is_staff === true || payload.staff === true,
    is_superuser: payload.is_superuser === true || payload.superuser === true,
    uu_id: payload.uu_id ?? payload.user_id ?? payload.sub ?? null,
  };

  if (!profile.name && !profile.email && !profile.uu_id && !profile.role) return null;
  return profile;
}

export function setStoredTokens(accessToken, refreshToken) {
  try {
    const previousAccessToken = window.localStorage.getItem(ACCESS_TOKEN_KEY);
    const nextPayload = decodeJwtPayload(accessToken);
    const previousPayload = decodeJwtPayload(previousAccessToken);

    const nextIdentity = String(
      nextPayload?.email || nextPayload?.preferred_username || nextPayload?.username || nextPayload?.sub || ''
    ).trim().toLowerCase();
    const previousIdentity = String(
      previousPayload?.email || previousPayload?.preferred_username || previousPayload?.username || previousPayload?.sub || ''
    ).trim().toLowerCase();

    if (nextIdentity && previousIdentity && nextIdentity !== previousIdentity) {
      window.localStorage.removeItem(PROFILE_CACHE_KEY);
      clearAuthenticatedQueryCache();
    }

    if (accessToken) window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) window.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

    if (nextPayload?.force_password_change === true) {
      window.localStorage.setItem(FORCE_PASSWORD_CHANGE_KEY, 'true');
    }
  } catch {
    /* private mode / storage disabled */
  }
}

export function setForcePasswordChangePending(isRequired) {
  try {
    if (isRequired) {
      window.localStorage.setItem(FORCE_PASSWORD_CHANGE_KEY, 'true');
    } else {
      window.localStorage.removeItem(FORCE_PASSWORD_CHANGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function isForcePasswordChangePending() {
  try {
    if (window.localStorage.getItem(FORCE_PASSWORD_CHANGE_KEY) === 'true') {
      return true;
    }
  } catch {
    /* ignore */
  }

  const payload = decodeJwtPayload(getStoredAccessToken());
  return payload?.force_password_change === true;
}

export function clearStoredTokens() {
  try {
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    window.localStorage.removeItem(FORCE_PASSWORD_CHANGE_KEY);
    window.localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch {
    /* ignore */
  }
  clearAuthenticatedQueryCache();
}

export function getStoredProfileCache() {
  try {
    const raw = window.localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function setStoredProfileCache(profile) {
  if (!profile || typeof profile !== 'object') return;
  try {
    window.localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
  } catch {
    /* ignore */
  }
}

export function setProfileEndpointMarkedMissing(isMissing) {
  try {
    if (isMissing) {
      window.sessionStorage.setItem(PROFILE_ENDPOINT_MISSING_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(PROFILE_ENDPOINT_MISSING_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function isNetworkHistoryEndpointUnavailable() {
  return isEndpointUnavailable(NETWORK_HISTORY_ENDPOINT_UNAVAILABLE_KEY);
}

export function setNetworkHistoryEndpointUnavailable(isUnavailable) {
  setEndpointUnavailable(NETWORK_HISTORY_ENDPOINT_UNAVAILABLE_KEY, isUnavailable);
}

export function isNetworkScheduleEndpointMarkedMissing() {
  return isEndpointUnavailable(NETWORK_SCHEDULE_ENDPOINT_MISSING_KEY);
}

export function setNetworkScheduleEndpointMarkedMissing(isMissing) {
  setEndpointUnavailable(NETWORK_SCHEDULE_ENDPOINT_MISSING_KEY, isMissing);
}

// Short-lived so a transient 404/5xx (e.g. during a redeploy) cannot hide data for the whole session.
export const ENDPOINT_UNAVAILABLE_TTL_MS = 60 * 1000;

export function isEndpointUnavailable(key) {
  try {
    const markedAt = Number(window.sessionStorage.getItem(key));
    if (Number.isFinite(markedAt) && markedAt > 0 && Date.now() - markedAt < ENDPOINT_UNAVAILABLE_TTL_MS) {
      return true;
    }
    window.sessionStorage.removeItem(key);
    return false;
  } catch {
    return false;
  }
}
export function setEndpointUnavailable(key, flag) {
  try {
    if (flag) window.sessionStorage.setItem(key, String(Date.now()));
    else window.sessionStorage.removeItem(key);
  } catch { /* ignore */ }
}

export function getNetworkScanAvailabilityStatus() {
  return {
    networkHistoryUnavailable: isNetworkHistoryEndpointUnavailable(),
    anyHistoryUnavailable: isNetworkHistoryEndpointUnavailable(),
  };
}

export function getEndpointAvailabilityStatus() {
  return {
    sitesUnavailable: isEndpointUnavailable(SITES_ENDPOINT_UNAVAILABLE_KEY),
    netScannersUnavailable: isEndpointUnavailable(NET_SCANNERS_ENDPOINT_UNAVAILABLE_KEY),
    webScannersUnavailable: isEndpointUnavailable(WEB_SCANNERS_ENDPOINT_UNAVAILABLE_KEY),
    vulnsUnavailable: isEndpointUnavailable(VULNS_ENDPOINT_UNAVAILABLE_KEY),
    historyUnavailable: isEndpointUnavailable(HISTORY_ENDPOINT_UNAVAILABLE_KEY),
    inventoryDetailUnavailable: isEndpointUnavailable(INVENTORY_DETAIL_ENDPOINT_UNAVAILABLE_KEY),
    earlyIntelUnavailable: isEndpointUnavailable(EARLY_INTEL_ENDPOINT_UNAVAILABLE_KEY),
    earlyIntelExposuresUnavailable: isEndpointUnavailable(EARLY_INTEL_EXPOSURES_ENDPOINT_UNAVAILABLE_KEY),
    webScanHistoryUnavailable: isEndpointUnavailable(WEB_SCAN_HISTORY_ENDPOINT_UNAVAILABLE_KEY),
  };
}

export function getProfileFallback(token = null) {
  const cached = getStoredProfileCache();
  const tokenPayload = decodeJwtPayload(token || getStoredAccessToken());
  const fromToken = buildProfileFromJwtPayload(tokenPayload);

  if (cached && fromToken) {
    return {
      ...fromToken,
      ...cached,
      name: cached.name || cached.display_name || fromToken.name || '',
      display_name: cached.display_name || cached.name || fromToken.display_name || fromToken.name || '',
      email: cached.email || fromToken.email || '',
    };
  }

  return cached || fromToken || null;
}

export function getCachedProfile() {
  return getStoredProfileCache();
}

export function hasUsableAccessToken() {
  const token = getStoredAccessToken();
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  const exp = Number(payload.exp);
  if (!Number.isFinite(exp)) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp > nowSeconds + 5;
}

// =============================================================================
// Token refresh
// =============================================================================

async function refreshAccessToken() {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(toApiUrl('/auth/token/refresh/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'omit',
      cache: 'no-store',
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      console.error('[AUTH] Token refresh failed', {
        status: response.status,
        statusText: response.statusText,
        error: errorText.slice(0, 200),
        timestamp: new Date().toISOString(),
      });
      return null;
    }

    const data = await parseJsonOrThrow(response, 'Token refresh');
    const nextAccess = data?.access || null;
    const nextRefresh = data?.refresh || refreshToken;
    if (!nextAccess) {
      console.warn('[AUTH] No access token in refresh response');
      return null;
    }

    setStoredTokens(nextAccess, nextRefresh);
    console.debug('[AUTH] Token refreshed successfully');
    return nextAccess;
  } catch (error) {
    console.error('[AUTH] Token refresh error', error);
    return null;
  }
}

export async function refreshAccessTokenWithMutex() {
  if (refreshInFlightPromise) {
    return refreshInFlightPromise;
  }

  refreshInFlightPromise = (async () => {
    const refreshed = await refreshAccessToken();
    return refreshed;
  })().finally(() => {
    refreshInFlightPromise = null;
  });

  return refreshInFlightPromise;
}

export async function ensureFreshAccessToken(minExpirySeconds = 300, token = null) {
  let authToken = token || (hasUsableAccessToken() ? getStoredAccessToken() : null);
  if (!authToken) {
    const refreshed = await refreshAccessTokenWithMutex();
    if (!refreshed) {
      return;
    }
    authToken = hasUsableAccessToken() ? getStoredAccessToken() : null;
    if (!authToken) return;
  }

  const payload = decodeJwtPayload(authToken);
  if (!payload) {
    return;
  }

  const secondsUntilExpiry = payload.exp - Math.floor(Date.now() / 1000);

  if (secondsUntilExpiry < minExpirySeconds) {
    console.debug('[AUTH] Token expiring soon, refreshing proactively', {
      secondsUntilExpiry,
      minExpirySeconds,
    });
    const refreshed = await refreshAccessTokenWithMutex();
    if (!refreshed) {
      console.warn('[AUTH] Proactive refresh failed; proceeding with existing token');
    } else {
      console.debug('[AUTH] Proactive token refresh successful');
    }
  }
}

export async function ensureAuthenticatedSession() {
  debugApiLog('[AUTH] ensureAuthenticatedSession called');
  
  if (hasUsableAccessToken()) {
    debugApiLog('[AUTH] Already have usable access token');
    return true;
  }

  try {
    debugApiLog('[AUTH] Probing session with /api-test/');
    const sessionProbe = await fetch(toApiUrl('/api-test/'), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      credentials: getCredentialsMode(),
      cache: 'no-store',
    });
    if (sessionProbe.ok) {
      debugApiLog('[AUTH] Session probe succeeded');
      return true;
    }
    debugApiLog('[AUTH] Session probe returned:', sessionProbe.status);
  } catch (err) {
    console.error('[AUTH] Session probe error:', err);
  }

  try {
    debugApiLog('[AUTH] Attempting to refresh access token');
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      debugApiLog('[AUTH] Token refresh failed');
      clearStoredTokens();
      return false;
    }
    return hasUsableAccessToken();
  } catch (err) {
    console.error('[AUTH] Token refresh error:', err);
    clearStoredTokens();
    return false;
  }
}

export function notifyAuthExpired(code = 'auth_expired') {
  try {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { code } }));
  } catch {
    /* ignore */
  }
}

export function subscribeToAuthExpired(handler) {
  if (typeof handler !== 'function') return () => {};
  const listener = (event) => handler(event?.detail?.code ?? 'auth_expired');
  window.addEventListener(AUTH_EXPIRED_EVENT, listener);
  return () => window.removeEventListener(AUTH_EXPIRED_EVENT, listener);
}

export async function handleUnauthorizedResponse(response) {
  if (response?.status !== 401) return false;
  const hadAccessToken = Boolean(getStoredAccessToken());
  clearStoredTokens();
  clearLoginTime();
  if (hadAccessToken) {
    let code = 'auth_expired';
    try {
      const cloned = response.clone();
      const body = await cloned.json();
      if (body?.code) code = body.code;
    } catch {
      /* non-JSON or already consumed body */
    }
    notifyAuthExpired(code);
  }
  return true;
}

// =============================================================================
// 3. URL and fetch options
// =============================================================================

export function defaultHeaders(token = null) {
  const headers = { 'Content-Type': 'application/json' };
  const authToken = token || (hasUsableAccessToken() ? getStoredAccessToken() : null);
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  return headers;
}

export function mergeAuthHeaders(headersInit = null, token = null) {
  const merged = new Headers(headersInit || {});
  const authToken = token || (hasUsableAccessToken() ? getStoredAccessToken() : null);
  if (authToken) {
    merged.set('Authorization', `Bearer ${authToken}`);
  }
  return merged;
}

export function getCredentialsMode() {
  if (hasUsableAccessToken()) return 'omit';
  return API_BASE_URL ? 'omit' : 'same-origin';
}

export function jsonFetchOptions(token = null) {
  return {
    headers: defaultHeaders(token),
    credentials: getCredentialsMode(token),
    cache: 'no-store',
  };
}

const SAFE_HTTP_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

function readCookie(name) {
  try {
    const prefix = `${name}=`;
    const match = String(document.cookie || '').split('; ').find((part) => part.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) : '';
  } catch {
    return '';
  }
}

async function isCsrfFailure(response) {
  if (response?.status !== 403) return false;
  try {
    return /csrf/i.test(await response.clone().text());
  } catch {
    return false;
  }
}

export async function fetchWithAuthRetry(url, init = {}, token = null) {
  const execute = async (tokenOverride = null) => {
    const headers = mergeAuthHeaders(init?.headers, tokenOverride || token);
    // With a bearer token the API must not also see the browser's session cookie:
    // Django would then authenticate by session and demand a CSRF token (403 on saves).
    const credentials = headers.has('Authorization') ? 'omit' : init?.credentials;
    const method = String(init?.method || 'GET').toUpperCase();
    if (credentials && credentials !== 'omit' && !SAFE_HTTP_METHODS.has(method) && !headers.has('X-CSRFToken')) {
      const csrfToken = readCookie('csrftoken');
      if (csrfToken) headers.set('X-CSRFToken', csrfToken);
    }
    const requestInit = {
      ...init,
      headers,
      credentials,
    };
    debugApiLog('[FETCH]', url, {
      credentials: requestInit.credentials,
      hasAuthHeader: !!requestInit.headers?.Authorization,
      headers: requestInit.headers,
    });
    return fetch(url, requestInit);
  };

  let response = await execute();
  const csrfRejected = await isCsrfFailure(response);
  if (response?.status !== 401 && !csrfRejected) {
    return response;
  }

  const hadSession = Boolean(getStoredAccessToken() || getStoredRefreshToken());
  if (!hadSession) {
    return response;
  }

  const refreshed = await refreshAccessTokenWithMutex();
  if (!refreshed) {
    await handleUnauthorizedResponse(response);
    return response;
  }

  response = await execute(refreshed);
  if (response?.status === 401) {
    await handleUnauthorizedResponse(response);
  }
  return response;
}

export function toApiUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (API_BASE_URL) {
    return `${API_BASE_URL}${normalizedPath}`;
  }
  return `/api/v1${normalizedPath}`;
}

export function toApiRootUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (API_ROOT_URL) {
    return `${API_ROOT_URL}${normalizedPath}`;
  }
  return normalizedPath;
}

function toWebSocketOrigin() {
  if (API_BASE_URL) {
    try {
      const apiUrl = new URL(API_BASE_URL);
      const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${apiUrl.host}`;
    } catch {
      /* ignore invalid API base URL and fall back to current window */
    }
  }

  if (typeof window === 'undefined') return '';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

export function appendScopeQuery(params, scopeQuery) {
  if (!scopeQuery || typeof scopeQuery !== 'object') return;
  Object.entries(scopeQuery).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    const normalized = String(value).trim();
    if (!normalized) return;
    params.set(String(key), normalized);
  });
}

export function getScanStatusWebSocketUrl(scopeQuery = null) {
  const origin = toWebSocketOrigin();
  if (!origin) return '';

  const params = new URLSearchParams();
  appendScopeQuery(params, scopeQuery);

  const accessToken = hasUsableAccessToken() ? getStoredAccessToken() : null;
  if (accessToken) {
    params.set('access_token', accessToken);
  }

  const queryString = params.toString();
  return `${origin}/api/ws/scans/status/${queryString ? `?${queryString}` : ''}`;
}

// =============================================================================
// 4. Response parsing
// =============================================================================

export function isLicenseGatedMessage(msg) {
  const lower = String(msg || '').toLowerCase();
  return (
    lower.includes('enterprise') ||
    lower.includes('license') ||
    lower.includes('subscription') ||
    lower.includes('upgrade') ||
    lower.includes('plan required')
  );
}

const DEFAULT_USER_FACING_ERROR = 'Unable to load this data right now. Please try again.';

/** Patterns that must never appear in customer-facing UI copy. */
const TECHNICAL_USER_MESSAGE_RE =
  /\/api\/v\d+|returned by\s+\/|expected\s+\/api|endpoint is (not available|currently unavailable)|backend deployment|backend legacy|react_app_|setupproxy|returned html instead of json|returned unexpected content|dev hint:|django terminal|check api connectivity|jwt|csrf|traceback|stack trace|^https?:\/\/\S+|^(get|post|put|patch|delete)\s+\/|^http\s+\d{3}\b/i;

function looksTechnicalUserMessage(text) {
  const value = String(text || '').trim();
  if (!value) return true;
  if (TECHNICAL_USER_MESSAGE_RE.test(value)) return true;
  if (/\bfailed\s*\(\d{3}\)/i.test(value)) return true;
  if (/\(\d{3}\)\.?$/i.test(value) && /(failed|fetch|error|unavailable)/i.test(value)) return true;
  return false;
}

function friendlyMessageForStatus(status) {
  const code = Number(status);
  if (code === 401) return 'Your session has expired. Please sign in again.';
  if (code === 403) return 'You do not have permission to view this.';
  if (code === 404) return 'No data is available for this view right now.';
  if (code === 408 || code === 504) return 'The request timed out. Please try again.';
  if (code === 429) return 'Too many requests. Please wait a moment and try again.';
  if (code >= 500) return 'The service is temporarily unavailable. Please try again shortly.';
  if (code >= 400) return DEFAULT_USER_FACING_ERROR;
  return null;
}

/**
 * Unwrap DRF / Python list-style error payloads into plain text.
 * e.g. ["OTP challenge expired."] or "['OTP challenge expired.']"
 */
function unwrapErrorMessagePayload(raw) {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item || '').trim()).filter(Boolean).join(' ').trim();
  }
  if (raw && typeof raw === 'object') {
    if (typeof raw.message === 'string' || Array.isArray(raw.message)) {
      return unwrapErrorMessagePayload(raw.message);
    }
    if (typeof raw.detail === 'string' || Array.isArray(raw.detail)) {
      return unwrapErrorMessagePayload(raw.detail);
    }
  }
  let text = String(raw ?? '').trim();
  if (!text) return '';
  const listMatch = text.match(/^\[\s*['"]([\s\S]*?)['"]\s*\]$/);
  if (listMatch) {
    text = String(listMatch[1] || '').trim();
  }
  return text;
}

/**
 * Sanitize API/service errors before showing them in the UI.
 * Strips endpoint paths, HTTP status jargon, and other backend details.
 */
export function toUserFacingErrorMessage(raw, fallback = DEFAULT_USER_FACING_ERROR, options = {}) {
  const safeFallback = (() => {
    const candidate = String(fallback || '').trim();
    if (!candidate || looksTechnicalUserMessage(candidate)) {
      return friendlyMessageForStatus(options?.status) || DEFAULT_USER_FACING_ERROR;
    }
    return candidate;
  })();

  const text = unwrapErrorMessagePayload(raw);
  if (!text) return safeFallback;
  if (/csrf/i.test(text)) return 'Your session has expired. Please sign in again.';

  if (looksTechnicalUserMessage(text)) {
    return safeFallback;
  }

  // Drop parenthetical HTTP status codes from otherwise readable messages.
  const withoutStatus = text.replace(/\s*\(\d{3}\)\.?$/g, '').trim();
  if (withoutStatus && looksTechnicalUserMessage(withoutStatus)) {
    return safeFallback;
  }
  return withoutStatus || safeFallback;
}

export function normalizePositiveSeconds(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.ceil(parsed);
}

function humanizeFieldName(key) {
  const label = String(key || '').replace(/_/g, ' ').trim();
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
}

function firstFieldMessage(data) {
  if (!data || typeof data !== 'object') return null;
  const skip = new Set(['detail', 'error', 'message', 'non_field_errors', 'error_code', 'retry_after_seconds']);
  for (const [key, value] of Object.entries(data)) {
    if (skip.has(key)) continue;
    if (Array.isArray(value) && value.length) return `${humanizeFieldName(key)}: ${value.map(String).join(' ')}`;
    if (typeof value === 'string' && value.trim()) return `${humanizeFieldName(key)}: ${value}`;
  }
  return null;
}

export async function readApiErrorMessage(response, fallback, options = {}) {
  const clearAuthOn401 = options.clearAuthOn401 !== false;
  if (response?.status === 401 && clearAuthOn401) {
    clearStoredTokens();
    notifyAuthExpired();
  }
  const statusOptions = { status: response?.status };
  try {
    const ct = response.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const data = await response.json();
      if (typeof data.detail === 'string') {
        return toUserFacingErrorMessage(data.detail, fallback, statusOptions);
      }
      if (Array.isArray(data.detail)) {
        return toUserFacingErrorMessage(data.detail.map(String).join(' '), fallback, statusOptions);
      }
      if (typeof data.error === 'string' && data.error.trim()) {
        return toUserFacingErrorMessage(data.error, fallback, statusOptions);
      }
      // Only client errors: 5xx `message` bodies may carry raw exception text.
      if (
        response.status >= 400 &&
        response.status < 500 &&
        typeof data.message === 'string' &&
        data.message.trim()
      ) {
        return toUserFacingErrorMessage(data.message, fallback, statusOptions);
      }
      if (data.non_field_errors) {
        const n = data.non_field_errors;
        return toUserFacingErrorMessage(
          Array.isArray(n) ? n.join(' ') : String(n),
          fallback,
          statusOptions
        );
      }
      const fieldMessage = firstFieldMessage(data);
      if (fieldMessage) {
        return toUserFacingErrorMessage(fieldMessage, fallback, statusOptions);
      }
      if (data.email != null) {
        const e = data.email;
        return toUserFacingErrorMessage(
          `Email: ${Array.isArray(e) ? e.join(' ') : String(e)}`,
          fallback,
          statusOptions
        );
      }
      if (data.password != null) {
        const p = data.password;
        return toUserFacingErrorMessage(
          `Password: ${Array.isArray(p) ? p.join(' ') : String(p)}`,
          fallback,
          statusOptions
        );
      }
      if (data.current_password != null) {
        const c = data.current_password;
        return toUserFacingErrorMessage(
          Array.isArray(c) ? c.join(' ') : String(c),
          fallback,
          statusOptions
        );
      }
    }
  } catch {
    /* ignore */
  }
  return toUserFacingErrorMessage(fallback, DEFAULT_USER_FACING_ERROR, statusOptions);
}

export async function readApiErrorData(response, fallback, options = {}) {
  const clearAuthOn401 = options.clearAuthOn401 !== false;
  if (response?.status === 401 && clearAuthOn401) {
    clearStoredTokens();
    notifyAuthExpired();
  }
  const statusOptions = { status: response?.status };
  try {
    const ct = response.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const data = await response.json();
      const message =
        (typeof data.detail === 'string' ? data.detail : null) ||
        (Array.isArray(data.detail) ? data.detail.map(String).join(' ') : null) ||
        (typeof data.error === 'string' && data.error.trim() ? data.error : null) ||
        (Array.isArray(data.error) ? data.error.map(String).join(' ') : null) ||
        (typeof data.message === 'string' && data.message.trim() ? data.message : null) ||
        (Array.isArray(data.message) ? data.message.map(String).join(' ') : null) ||
        (data.non_field_errors
          ? (Array.isArray(data.non_field_errors)
              ? data.non_field_errors.map(String).join(' ')
              : String(data.non_field_errors))
          : null) ||
        firstFieldMessage(data) ||
        fallback;
      const retryAfterSeconds = normalizePositiveSeconds(data?.retry_after_seconds);
      return {
        message: toUserFacingErrorMessage(message, fallback, statusOptions),
        errorCode: data?.error_code || null,
        retryAfterSeconds,
      };
    }
  } catch {
    /* ignore */
  }
  return {
    message: toUserFacingErrorMessage(fallback, DEFAULT_USER_FACING_ERROR, statusOptions),
    errorCode: null,
    retryAfterSeconds: null,
  };
}

export function normalizeMfaMethod(method) {
  const safe = String(method || '').trim().toLowerCase();
  if (safe === 'app' || safe === 'authenticator' || safe === 'auth_app') return 'totp';
  return safe;
}

export async function parseJsonOrThrow(response, apiName = 'API') {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    if (text.trim().toLowerCase().startsWith('<!doctype') || text.trim().startsWith('<!DOCTYPE')) {
      throw new Error(
        toUserFacingErrorMessage(
          `${apiName} returned HTML instead of JSON`,
          'Unable to reach the service right now. Please try again.',
          { status: response?.status }
        )
      );
    }
    throw new Error(
      toUserFacingErrorMessage(
        `${apiName} returned unexpected content (${response.status})`,
        DEFAULT_USER_FACING_ERROR,
        { status: response?.status }
      )
    );
  }
  return response.json();
}

// =============================================================================
// 5. Shared normalizers and utilities
// =============================================================================

export function normalizeApiList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.serializer)) return data.serializer;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.history)) return data.history;
  if (Array.isArray(data?.items)) return data.items;
  if (data && typeof data === 'object') return [data];
  return [];
}

export function parseScanStatusNumber(value) {
  const raw = String(value ?? '').trim().replace(/%/g, '');
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isActiveRunningScan(row) {
  const lifecycle = String(row?.task_lifecycle ?? row?.taskLifecycle ?? '').trim().toLowerCase();
  if (
    lifecycle === 'completed' ||
    lifecycle === 'interrupted' ||
    lifecycle === 'failed' ||
    lifecycle === 'aborted' ||
    lifecycle === 'cancelled' ||
    lifecycle === 'stopped' ||
    lifecycle === 'scheduled'
  ) {
    return false;
  }
  if (
    lifecycle === 'queued' ||
    lifecycle === 'starting' ||
    lifecycle === 'running' ||
    lifecycle === 'in_progress' ||
    lifecycle === 'launching' ||
    lifecycle === 'recovering'
  ) {
    return true;
  }

  const statusRaw = row?.scan_status ?? row?.status ?? row?.progress ?? '';
  const statusText = String(statusRaw ?? '').trim().toLowerCase();
  const numericStatus = parseScanStatusNumber(statusRaw);

  if (!statusText && numericStatus == null) return false;

  if (numericStatus != null) {
    return numericStatus >= 0 && numericStatus < 100;
  }

  if (
    statusText.includes('completed') ||
    statusText.includes('complete') ||
    statusText.includes('finished') ||
    statusText.includes('done') ||
    statusText.includes('success') ||
    statusText.includes('stopped') ||
    statusText.includes('interrupted') ||
    statusText.includes('failed') ||
    statusText.includes('cancel') ||
    statusText.includes('scheduled')
  ) {
    return false;
  }

  return (
    statusText.includes('progress') ||
    statusText.includes('running') ||
    statusText.includes('processing') ||
    statusText.includes('active') ||
    statusText.includes('started')
  );
}

export function normalizeOrganizationRows(data) {
  const rows = normalizeApiList(data);
  return rows.map((row) => ({
    ...row,
    id: row?.id ?? row?.organization_id ?? row?.org_id ?? row?.uu_id ?? row?.org_uuid ?? row?.organization_uuid ?? null,
    uu_id: row?.uu_id ?? row?.org_uuid ?? row?.organization_uuid ?? null,
    name: row?.name ?? row?.org_name ?? '',
  }));
}

export function compactObject(input) {
  return Object.fromEntries(
    Object.entries(input || {}).filter(([, value]) => value !== null && value !== undefined && value !== '')
  );
}

export function formatDateObjectToXspectreScheduleTime(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return 'now';
  const pad = (n) => String(n).padStart(2, '0');
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const minutes = pad(date.getMinutes());
  const period = date.getHours() >= 12 ? 'PM' : 'AM';
  let hour12 = date.getHours() % 12;
  if (hour12 === 0) hour12 = 12;
  return `${day}/${month}/${year} ${pad(hour12)}:${minutes}:00 ${period}`;
}

export function normalizeRoleToBackendRoleId(roleValue) {
  const raw = String(roleValue || '').trim().toLowerCase();
  if (!raw) return null;
  if (raw === '1' || raw === 'admin' || raw === 'administrator') return 'Admin';
  if (raw === '2' || raw === 'analyst') return 'Analyst';
  if (raw === '3' || raw === 'viewer') return 'Viewer';
  if (raw === '4' || raw === 'partner') return 'Partner';
  return String(roleValue).trim();
}

export function extractAnyId(payload, keys) {
  for (const key of keys) {
    const value = payload?.[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return '';
}

export async function requestJson(method, path, payload = null, token = null, query = null) {
  const qs = query && typeof query === 'object' && Object.keys(query).length > 0
    ? `?${new URLSearchParams(query).toString()}`
    : '';

  const normalizedMethod = String(method || '').trim().toUpperCase();
  const normalizedPath = String(path || '').trim();
  const isScanStartRequest =
    normalizedMethod === 'POST' &&
    (normalizedPath === '/networkscan/schedule/' || normalizedPath === '/webscan/schedule/');

  if (isScanStartRequest) {
    await ensureFreshAccessToken(300, token);
  }

  const response = await fetchWithAuthRetry(toApiUrl(`${path}${qs}`), {
    method,
    ...jsonFetchOptions(token),
    body: payload != null ? JSON.stringify(payload) : undefined,
  }, token);

  if (!response.ok) {
    const msg = await readApiErrorMessage(
      response,
      'Unable to complete this request. Please try again.'
    );
    throw new Error(msg);
  }

  if (response.status === 204) return null;
  return parseJsonOrThrow(response, 'Request');
}

export function formatPasswordVerificationFailureMessage(raw) {
  const t = String(raw || '').trim();
  if (!t) {
    return 'That password was not accepted. Please check it and try again.';
  }
  const lower = t.toLowerCase();
  if (
    (lower.includes('no active account') && lower.includes('credential')) ||
    lower.includes('unable to log in') ||
    lower.includes('invalid credentials')
  ) {
    return 'That password does not match your signed-in account. Please try again.';
  }
  if (lower.includes('current password is incorrect')) {
    return 'That password does not match your signed-in account. Please try again.';
  }
  if (lower.includes('authentication credentials were not provided')) {
    return 'Your session could not be verified. Sign in again, then retry this action.';
  }
  return t;
}

export async function verifyPasswordWithLoginProbe(email, password) {
  const safeEmail = String(email || '').trim();
  const safePassword = String(password || '');
  if (!safeEmail || !safePassword) {
    throw new Error('Email and password are required for verification.');
  }
  const response = await fetch(toApiUrl('/auth/token/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    body: JSON.stringify({
      email: safeEmail,
      username: safeEmail,
      password: safePassword,
    }),
  });
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Password verification failed (${response.status})`, {
      clearAuthOn401: false,
    });
    throw new Error(formatPasswordVerificationFailureMessage(msg));
  }
  await parseJsonOrThrow(response, 'Login');
}
